import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchMe, login as apiLogin, logout as apiLogout, setUnauthorizedHandler } from "../api";
import { shouldRedirectToLogin } from "../api/authRedirect";
import { getDefaultPathForUser, hasPermission } from "../permissions";

const AuthContext = createContext(null);

const ROLE_LABELS = {
  manager: "Руководитель",
  admin: "Админ",
};

/** @deprecated Используйте getDefaultPathForUser(user) */
export function getDefaultPathForRole(role) {
  return role === "admin" ? "/tasks" : "/";
}

export { getDefaultPathForUser };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const data = await fetchMe();
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe().finally(() => setLoading(false));
  }, [refreshMe]);

  useEffect(() => {
    setUnauthorizedHandler((error) => {
      const url = error?.config?.url || "";
      if (!shouldRedirectToLogin(url)) {
        return;
      }
      setUser(null);
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (username, password) => {
    const data = await apiLogin(username, password);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
    }
  }, []);

  const can = useCallback(
    (code) => hasPermission(user, code),
    [user]
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      refreshMe,
      can,
      roleLabel: user ? ROLE_LABELS[user.role] || user.role : null,
    }),
    [user, loading, login, logout, refreshMe, can]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
